// ============ Team Tab - Gestione Membri e Inviti ============

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { UserPlus, Trash2, Mail, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Feedback,
  FormField,
  IconButton,
  LoadingBlock,
  SectionHeader,
  Select,
  SettingsCard,
  TextInput,
  type FeedbackProps,
} from './_shared';

// =============================================================================
// Types
// =============================================================================

interface Member {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'OWNER' | 'ADMIN' | 'WORKER';
  isActive: boolean;
  joinedAt: string;
}

interface Invite {
  id: string;
  email: string;
  role: 'OWNER' | 'ADMIN' | 'WORKER';
  status: string;
  expiresAt: string;
  createdAt: string;
}

const roleBadgeTone: Record<Member['role'], 'purple' | 'blue' | 'gray'> = {
  OWNER: 'purple',
  ADMIN: 'blue',
  WORKER: 'gray',
};

// =============================================================================
// Component
// =============================================================================

export function TeamTab() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'ADMIN' | 'WORKER'>('WORKER');
  const [feedback, setFeedback] = useState<FeedbackProps | null>(null);

  // --- Queries ---

  const {
    data: members = [],
    isLoading: loadingMembers,
  } = useQuery<Member[]>({
    queryKey: ['tenant-members'],
    queryFn: async () => {
      const res = await apiClient.get('/tenants/members');
      return res.data;
    },
  });

  const {
    data: invites = [],
    isLoading: loadingInvites,
  } = useQuery<Invite[]>({
    queryKey: ['tenant-invites'],
    queryFn: async () => {
      const res = await apiClient.get('/tenants/invites');
      return res.data;
    },
  });

  // --- Mutations ---

  const inviteMutation = useMutation({
    mutationFn: async (data: { email: string; role: string }) => {
      const res = await apiClient.post('/tenants/invite', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-invites'] });
      setInviteEmail('');
      setFeedback({ type: 'success', message: t('team.inviteSuccess') });
      setTimeout(() => setFeedback(null), 3000);
    },
    onError: (err: any) => {
      const msg =
        err.response?.data?.message || t('team.inviteError');
      setFeedback({
        type: 'error',
        message: typeof msg === 'string' ? msg : msg[0],
      });
      setTimeout(() => setFeedback(null), 5000);
    },
  });

  const revokeInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      await apiClient.delete(`/tenants/invites/${inviteId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-invites'] });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/tenants/members/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-members'] });
    },
  });

  // --- Handlers ---

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    inviteMutation.mutate({ email: inviteEmail, role: inviteRole });
  };

  // --- Render ---

  return (
    <div className="space-y-6">
      {/* Invita Membro */}
      <SettingsCard>
        <SectionHeader icon={UserPlus} title={t('team.inviteMember')} />

        <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <FormField label={t('common:fields.email')}>
              <TextInput
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('team.emailPlaceholder')}
                required
              />
            </FormField>
          </div>
          <div className="sm:w-40">
            <FormField label={t('team.role')}>
              <Select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as 'ADMIN' | 'WORKER')}
              >
                <option value="WORKER">{t('team.roleWorker')}</option>
                <option value="ADMIN">{t('team.roleAdmin')}</option>
              </Select>
            </FormField>
          </div>
          <Button
            type="submit"
            loading={inviteMutation.isPending}
            disabled={!inviteEmail.trim()}
            icon={Mail}
          >
            {t('team.invite')}
          </Button>
        </form>

        {feedback && (
          <div className="mt-4">
            <Feedback {...feedback} />
          </div>
        )}
      </SettingsCard>

      {/* Membri Attuali */}
      <SettingsCard>
        <SectionHeader icon={Shield} title={t('team.members')} />

        {loadingMembers ? (
          <LoadingBlock />
        ) : (
          <ul className="divide-y divide-gray-100">
            {members.map((member) => (
              <li
                key={member.id}
                className="py-3 flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-emerald-50 text-emerald-700 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {member.firstName[0]}
                    {member.lastName[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {member.firstName} {member.lastName}
                    </p>
                    <p className="text-sm text-gray-500 truncate">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Badge tone={roleBadgeTone[member.role]}>
                    {t(`common:roles.${member.role}`)}
                  </Badge>
                  {member.role !== 'OWNER' && (
                    <IconButton
                      icon={Trash2}
                      tone="danger"
                      label={t('team.removeMember')}
                      onClick={() => {
                        if (
                          window.confirm(
                            t('team.removeConfirm', {
                              name: `${member.firstName} ${member.lastName}`,
                            }),
                          )
                        ) {
                          removeMemberMutation.mutate(member.userId);
                        }
                      }}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </SettingsCard>

      {/* Inviti Pendenti */}
      {(loadingInvites || invites.length > 0) && (
        <SettingsCard>
          <SectionHeader icon={Mail} title={t('team.pendingInvites')} />

          {loadingInvites ? (
            <LoadingBlock />
          ) : (
            <ul className="divide-y divide-gray-100">
              {invites.map((invite) => (
                <li key={invite.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{invite.email}</p>
                    <p className="text-sm text-gray-500">
                      {t('team.inviteExpires', {
                        role: t(`common:roles.${invite.role}`),
                        date: new Date(invite.expiresAt).toLocaleDateString(),
                      })}
                    </p>
                  </div>
                  <IconButton
                    icon={Trash2}
                    tone="danger"
                    label={t('team.revokeInvite')}
                    onClick={() => revokeInviteMutation.mutate(invite.id)}
                    disabled={revokeInviteMutation.isPending}
                  />
                </li>
              ))}
            </ul>
          )}
        </SettingsCard>
      )}
    </div>
  );
}
